import { useState, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
	CardElement,
	Elements,
	ElementsConsumer,
} from "@stripe/react-stripe-js";

import { API_URL, getValFromLS } from "../../helpers";

import "./styles.css";

import Msg from "../../components/Msg";
import Submit from "../../components/Submit";

const CCForm = ({ stripe, elements, added, history }) => {
	const [cardComplete, setCardComplete] = useState(false);
	const [, setErrors] = useState([]);
	const [names] = useState({});
	const [msg, setMsg] = useState({});

	const formElement = useRef();

	const handleCardElementChange = (e) => {
		setCardComplete(e.complete);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setErrors([]);
		setMsg({
			type: "working",
			text: "",
		});

		if (!cardComplete) {
			setMsg({
				type: "error",
				text: "Please enter complete information for your credit card.",
			});

			return false;
		}

		const url = new URL(`${API_URL}/dashboardHotelStripe`);
		let data = new FormData();
		Object.entries(names).forEach(([key, value]) => {
			data.append(key, value);
		});
		data.append("auth", getValFromLS("dash", true));
		data.append("fromAdmin", getValFromLS("vtoUserToken") ? "1" : "0");

		try {
			const response = await fetch(url, {
				method: "POST",
				body: data,
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				handlePayment(
					json.name,
					json.email,
					json.stripeCustomerId,
					json.stripeIntent
				);
			} else {
				setErrors(json.fields);
				setMsg({
					type: "error",
					text: json.text,
				});
			}
		} catch (error) {
			setMsg({
				type: "error",
				text: "An error has occurred.",
			});
		}
	};

	const handlePayment = async (name, email, id, intent) => {
		if (elements == null) {
			return false;
		}

		const { error, setupIntent } = await stripe.confirmCardSetup(intent, {
			payment_method: {
				card: elements.getElement(CardElement),
				billing_details: {
					name: name,
					email: email,
				},
			},
		});

		if (error) {
			setMsg({
				type: "error",
				text: error.message,
			});
		} else {
			handleSuccess(id, setupIntent.payment_method);
		}
	};

	const handleSuccess = async (customerId, paymentMethodId) => {
		let data = new FormData();
		data.append("customerId", customerId);
		data.append("paymentMethodId", paymentMethodId);
		data.append("auth", getValFromLS("dash", true));
		data.append("fromAdmin", getValFromLS("vtoUserToken") ? "1" : "0");

		try {
			const response = await fetch(`${API_URL}/dashboardHotelStripeSubmit`, {
				method: "POST",
				body: data,
			});
			const json = await response.json();
			if (json && json.resp === 1) {
				added(json.stripe);
			} else {
				setMsg({
					type: "error",
					text: json.text,
				});
			}
		} catch (error) {
			console.warn("Error", error);
		}
	};

	return (
		<>
			<p>
				A credit card number on file is required to secure reservations at the
				JW Marriott. Entering the card number here authorizes the hotel to
				charge one night’s room, resort fee and tax deposit per reservation
				canceled or dates modified within the hotel’s cancelation policy.
			</p>

			<p>
				The hotel will collect each guest’s credit card for room charges and
				incidentals upon check-in.
			</p>

			<p>
				Should you have any questions, please email{" "}
				<a href="mailto:valerobfc@valero.com">valerobfc@valero.com</a>, do not
				call the hotel directly.
			</p>

			<div className="spacer"></div>

			<form
				ref={formElement}
				method="post"
				action="/"
				onSubmit={(e) => handleSubmit(e)}
				className="well"
			>
				<h4
					style={{
						marginTop: "0",
						lineHeight: "22px",
						marginBottom: "10px",
						fontSize: "1.1rem",
					}}
				>
					Add Credit Card
				</h4>

				<CardElement onChange={handleCardElementChange} />

				<div className="msg-submit">
					{msg.type && <Msg data={msg} />}

					<div
						className={
							msg.type === "working" || msg.type === "success"
								? "hidden"
								: "submit-container"
						}
					>
						<Submit submit="Add Credit Card" />
					</div>
				</div>
			</form>
		</>
	);
};

const InjectedCCForm = ({ added, history }) => (
	<ElementsConsumer>
		{({ stripe, elements }) => (
			<CCForm
				stripe={stripe}
				elements={elements}
				added={added}
				history={history}
			/>
		)}
	</ElementsConsumer>
);

/*
  Stripe.setPublishableKey('pk_test_51KEapYCwW9CT7DTd1DFj04ga6Zh6Pl1zCe7dNUFVvVhbeDGbmZ8VNZncUW4JHPLcDcBtCgsWDEvGQmZpSKOuThMZ00WI1g4hJo'); // test publishable key
  Stripe.setPublishableKey('pk_live_51KEapYCwW9CT7DTdbcV8lvAKxTU3aciy7KiHUAN2ngeSM8rNIRUt9LJiBKrYav3L9ICp5R9nl45Ok6xujiNyyjPF00PadzTEid'); // live publishable key
*/
const stripePromise = loadStripe(
	"pk_live_51KEapYCwW9CT7DTdbcV8lvAKxTU3aciy7KiHUAN2ngeSM8rNIRUt9LJiBKrYav3L9ICp5R9nl45Ok6xujiNyyjPF00PadzTEid"
);

const Stripe = ({ added, history }) => (
	<Elements stripe={stripePromise}>
		<InjectedCCForm added={added} history={history} />
	</Elements>
);

export default Stripe;
